import React, { useState, useRef, useEffect } from 'react';

function DeleteConfirm({ workspace, onConfirm, onCancel }) {
  const [value, setValue] = useState('');
  return (
    <div className="ws-delete-confirm" onClick={e => e.stopPropagation()}>
      <div className="ws-delete-title">Delete "{workspace.name}"?</div>
      <div className="ws-delete-warning">This permanently deletes the workspace and all its pipeline data. Type <strong>delete</strong> to confirm.</div>
      <input
        autoFocus
        className="ws-delete-input"
        placeholder="delete"
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && value === 'delete') onConfirm(); if (e.key === 'Escape') onCancel(); }}
      />
      <div className="ws-delete-actions">
        <button className="ws-delete-btn-confirm" disabled={value !== 'delete'} onClick={onConfirm}>Delete workspace</button>
        <button className="ws-delete-btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function WorkspaceSelector({ workspace, workspaces, onSelect, onCreate, onDelete }) {
  const [open,     setOpen]     = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName,  setNewName]  = useState('');
  const [deleting, setDeleting] = useState(null); // workspace object
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setDeleting(null); setCreating(false); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    await onCreate(newName.trim());
    setNewName('');
    setCreating(false);
    setOpen(false);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    await onDelete(deleting.id);
    setDeleting(null);
    setOpen(false);
  };

  return (
    <div className="workspace-selector" ref={ref}>
      <button className="workspace-trigger" onClick={() => { setOpen(o => !o); setDeleting(null); setCreating(false); }}>
        <span className="workspace-icon">◈</span>
        <span className="workspace-name">{workspace?.name || 'Select workspace'}</span>
        <span className="workspace-chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="workspace-dropdown">
          {deleting ? (
            <DeleteConfirm
              workspace={deleting}
              onConfirm={handleDelete}
              onCancel={() => setDeleting(null)}
            />
          ) : (
            <>
              {workspaces.map(ws => (
                <div key={ws.id} className={`workspace-option-row ${workspace?.id === ws.id ? 'active' : ''}`}>
                  <button
                    className="workspace-option"
                    onClick={() => { onSelect(ws); setOpen(false); }}
                  >
                    <span className="workspace-option-icon">◈</span>
                    {ws.name}
                  </button>
                  {workspaces.length > 1 && (
                    <button
                      className="workspace-delete-icon"
                      title="Delete workspace"
                      onClick={e => { e.stopPropagation(); setDeleting(ws); setCreating(false); }}
                    >
                      🗑
                    </button>
                  )}
                </div>
              ))}

              {!creating && (
                <button className="workspace-create-btn" onClick={() => setCreating(true)}>
                  + New workspace
                </button>
              )}

              {creating && (
                <form className="workspace-create-form" onSubmit={handleCreate}>
                  <input
                    autoFocus
                    type="text"
                    placeholder="Workspace name"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    className="workspace-create-input"
                  />
                  <div className="workspace-create-actions">
                    <button type="submit" className="workspace-create-confirm">Create</button>
                    <button type="button" className="workspace-create-cancel" onClick={() => setCreating(false)}>Cancel</button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
